import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  Users,
  Quote,
  Lightbulb,
  Save,
  Gauge,
  Sparkles,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/misc';

const features = [
  {
    icon: Gauge,
    title: 'AI visibility analysis',
    body: 'See how your website and brand may appear in AI-generated answers across common buying journeys.',
  },
  {
    icon: Quote,
    title: 'Mentions & recommendations',
    body: 'Understand when your brand is named, recommended, or left out — and why it matters.',
  },
  {
    icon: Users,
    title: 'Competitor share of voice',
    body: 'Measure how often competitors show up alongside you and where they win attention.',
  },
  {
    icon: BarChart3,
    title: 'Citation & source intelligence',
    body: 'Surface the sources and references that shape how answers describe your category.',
  },
  {
    icon: Lightbulb,
    title: 'Actionable optimization',
    body: 'Get practical, prioritized recommendations you can act on to improve visibility.',
  },
  {
    icon: Save,
    title: 'Saved reports & tracking',
    body: 'Create an account to save projects and track how your visibility changes over time.',
  },
];

export function Landing() {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-20 sm:px-6 sm:pt-28">
          <div className="mx-auto max-w-3xl text-center">
            <Badge tone="blue" className="mb-6 inline-flex gap-1.5">
              <Sparkles size={13} /> Free during the launch period
            </Badge>
            <h1 className="text-balance text-4xl font-extrabold tracking-tight text-text-primary sm:text-6xl">
              Move your brand forward in{' '}
              <span className="bg-gradient-to-r from-accent-blue via-accent-indigo to-accent-cyan bg-clip-text text-transparent">
                AI search
              </span>
              .
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-text-secondary">
              Measure how your website appears in AI-generated answers, understand competitor
              visibility, and uncover practical opportunities to improve.
            </p>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link to="/analyze">
                <Button size="lg">
                  Analyze your website
                  <ArrowRight size={18} />
                </Button>
              </Link>
              <Link to="/sample-report">
                <Button size="lg" variant="secondary">
                  View sample report
                </Button>
              </Link>
            </div>
            <p className="mt-4 text-sm text-text-muted">
              No account required to run your first analysis.
            </p>
          </div>

          {/* Hero visual */}
          <div className="mx-auto mt-16 max-w-5xl">
            <div className="panel-raised overflow-hidden p-2 shadow-glow">
              <div className="rounded-lg border border-border bg-bg p-6">
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  {[
                    { k: 'Visibility Score', v: '78.4', s: '/100' },
                    { k: 'Mentions', v: '1,248' },
                    { k: 'Recommendations', v: '230' },
                    { k: 'Share of Voice', v: '38.6%' },
                  ].map((m) => (
                    <div key={m.k} className="panel p-4">
                      <p className="text-xs text-text-secondary">{m.k}</p>
                      <p className="mt-1 text-2xl font-bold text-text-primary">
                        {m.v}
                        {m.s && <span className="text-sm text-text-muted">{m.s}</span>}
                      </p>
                      <div className="mt-3 h-10 rounded bg-gradient-to-t from-accent-blue/20 to-transparent" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Accurate product framing */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="panel p-8 text-center">
          <p className="mx-auto max-w-3xl text-lg text-text-secondary">
            Visiward analyzes how your website and brand{' '}
            <span className="text-text-primary">may be represented in AI-generated answers</span> —
            giving you a clear, practical view of your visibility and where to improve.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="mb-10 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-text-primary">
            Everything you need to understand AI visibility
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {features.map((f) => (
            <div key={f.title} className="panel p-6 transition hover:border-accent-blue/40">
              <span className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-accent-blue/10 text-accent-blue">
                <f.icon size={20} />
              </span>
              <h3 className="mb-2 text-base font-semibold text-text-primary">{f.title}</h3>
              <p className="text-sm text-text-secondary">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Launch-free callout */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="panel-raised relative overflow-hidden p-10 text-center">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-accent-blue/5 via-transparent to-accent-cyan/5" />
          <div className="relative">
            <h2 className="text-3xl font-bold tracking-tight text-text-primary">
              Ready to improve your visibility?
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-text-secondary">
              Run a full analysis in minutes. Create a free account to save reports and track changes
              over time.
            </p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-text-secondary">
              {['Launch-free access', 'Save projects & history', 'Actionable recommendations'].map(
                (t) => (
                  <span key={t} className="inline-flex items-center gap-1.5">
                    <CheckCircle2 size={15} className="text-accent-teal" /> {t}
                  </span>
                ),
              )}
            </div>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link to="/analyze">
                <Button size="lg">Analyze your website</Button>
              </Link>
              <Link to="/register">
                <Button size="lg" variant="secondary">
                  Create free account
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
